import { Button } from '@app/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { RouteErrorComponent } from '../components/route-error';
import { BrandLink } from '../components/BrandLink';
import { SiteFooter } from '../components/site-footer';

export const Route = createFileRoute('/terms')({
  component: Terms,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function Terms() {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b">
        <BrandLink />
        <div className="flex gap-3">
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 py-16 px-6">
        <div className="max-w-3xl mx-auto prose prose-neutral dark:prose-invert">
          <h1>Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Last updated: April 20, 2026</p>

          <h2>Marketplace Fees</h2>
          <p>
            NilBoard earns revenue from two sources: subscription plans (for athletes, agents,
            and universities) and marketplace fees on deals originated through the platform.
            Subscription fees and marketplace fees are independent — athletes on the free tier
            can participate in the marketplace, and subscribers are not obligated to use the
            marketplace.
          </p>

          <h3>Tier 1 · Athlete → Brand</h3>
          <p>
            When an athlete sources a deal themselves using NilBoard's discovery, outreach, or
            valuation tools, NilBoard collects a small take-rate at closing. Our T1 rate is
            materially below the industry standard of 20% so athletes keep more of what they
            earn.
          </p>

          <h3>Tier 2 · Brand → Athlete</h3>
          <p>
            When a brand discovers a vetted athlete through NilBoard's marketplace, NilBoard
            collects a larger take-rate at closing plus a residual on repeat deals from the same
            brand within a defined window. The residual compensates NilBoard for the initial
            match.
          </p>

          <h3>Rate Transparency and Changes</h3>
          <p>
            Exact rates and residual windows are displayed on the{' '}
            <Link to="/pricing" className="underline">
              pricing page
            </Link>{' '}
            and at deal creation time. Rates may change with 30 days' notice via email and an
            in-app banner; changes do not apply to deals already in progress.
          </p>

          <h2>SMS Terms of Service</h2>
          <p>
            The following terms apply to NilBoard SMS notifications. By opting in to SMS in your
            account settings, you agree to these terms.
          </p>

          <h3>1. Program Description</h3>
          <p>
            NilBoard sends opt-in SMS notifications related to your NIL deals, including payment
            reminders, deal deadline alerts, and account notifications. You must opt in to receive
            these messages through your NilBoard account settings.
          </p>

          <h3>2. Opt-Out / Cancellation</h3>
          <p>
            You can cancel the SMS service at any time. Just text <strong>"STOP"</strong> to the
            short code. After you send the SMS message "STOP" to us, we will send you an SMS message
            to confirm that you have been unsubscribed. After this, you will no longer receive SMS
            messages from us. If you want to join again, just sign up as you did the first time and
            we will start sending SMS messages to you again.
          </p>

          <h3>3. Help</h3>
          <p>
            If you are experiencing issues with the messaging program you can reply with the keyword{' '}
            <strong>HELP</strong> for more assistance, or you can get help directly at{' '}
            <a href="mailto:support@nilboard.app">support@nilboard.app</a>.
          </p>

          <h3>4. Carrier Liability</h3>
          <p>Carriers are not liable for delayed or undelivered messages.</p>

          <h3>5. Message and Data Rates</h3>
          <p>
            As always, message and data rates may apply for any messages sent to you from us and to
            us from you. You will receive messages with a frequency that varies based on your deal
            activity. If you have any questions about your text plan or data plan, it is best to
            contact your wireless provider.
          </p>

          <h3>6. Privacy</h3>
          <p>
            If you have any questions regarding privacy, please read our privacy policy:{' '}
            <Link to="/privacy" className="underline">
              https://nilboard.app/privacy
            </Link>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
